import { useCallback, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { Pagination } from '@aimsos/ui';
import { Inbox, SearchX } from 'lucide-react';

import { AccessDeniedState, EmptyState, ErrorState } from '../components/states/index.js';
import DataListFilterBar from '../components/DataListFilterBar.jsx';
import DataListFilterSlideout from '../components/DataListFilterSlideout.jsx';
import EntityListSkeleton from '../components/EntityListSkeleton.jsx';
import EntityRowCard from '../components/EntityRowCard.jsx';
import { useDataListFilters } from '../hooks/useDataListFilters.js';
import { useListPagination, usePaginationLabels } from '../hooks/useListPagination.js';
import { useProfileTabData } from '../hooks/useProfileTabData.js';

const PAGE_SIZE_OPTIONS = [10, 15, 20, 50];

/**
 * The renderer for every DYNAMIC tab.
 *
 * Overview is the only tab that paints a dashboard of widgets. Everything else
 * — Garage, Appointments, Activity, whatever a tenant adds next — lists data
 * with filters and search, and they all come through here. One renderer, no
 * per-tab code.
 *
 * The filters are DATA: their ids, labels and options come down with the rows,
 * so a tenant adding a filter needs no release. The frontend only holds the
 * selected values, which is state the user owns.
 *
 * Rows render through the same card as the entity list, because a tab row and
 * an entity row are the same normalized contract — and for the same reason the
 * toolbar, the filter state and the all-filters panel are the entity list's,
 * not a second set that drifts from it.
 */
function ProfileDataListTab({ tab }) {
  const { t } = useTranslation('unified-profiles');
  const { entityType, entityId } = useOutletContext();

  const [isAllFiltersOpen, setIsAllFiltersOpen] = useState(false);

  const filtersState = useDataListFilters();
  const { filters, search, orderBy, orderDirection, hasActiveQuery, clearAll } = filtersState;

  const queryKey = `${orderBy}|${orderDirection}|${JSON.stringify(filters ?? {})}|${search?.query ?? ''}`;
  const { page, pageSize, handlePrevPage, handleNextPage, handlePageSizeChange } = useListPagination({
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    queryKey,
  });

  const { items, filterDefs, sortDefs, meta, loading, error, accessDenied, refetch } =
    useProfileTabData({
      entityType,
      entityId,
      tabId: tab.id,
      page,
      pageSize,
      orderBy,
      orderDirection,
      filters,
      search,
    });

  const { rangeLabel, labels: paginationLabels } = usePaginationLabels({ meta, itemCount: items.length });

  const openAllFilters = useCallback(() => {
    filtersState.openDraft();
    setIsAllFiltersOpen(true);
  }, [filtersState]);
  const closeAllFilters = useCallback(() => setIsAllFiltersOpen(false), []);

  const results = () => {
    if (loading) return <EntityListSkeleton perPage={pageSize} />;
    if (accessDenied) return <AccessDeniedState />;
    if (error) return <ErrorState message={error} onRetry={refetch} />;
    if (items.length === 0) {
      // "Nothing matched" and "nothing here" are different messages: telling
      // someone to change their search when the tab is simply empty is noise.
      return hasActiveQuery ? (
        <EmptyState
          testId="no-results-state"
          icon={SearchX}
          title={t('tabList.noResults.title')}
          subtitle={t('tabList.noResults.subtitle')}
        />
      ) : (
        <EmptyState
          icon={Inbox}
          title={t('tabList.empty.title')}
          subtitle={t('tabList.empty.subtitle', { tab: tab.label })}
        />
      );
    }

    return (
      <div className="flex flex-col gap-3">
        {items.map((row) => (
          <EntityRowCard key={row.id} entity={row} />
        ))}
      </div>
    );
  };

  const showPagination = !loading && !error && !accessDenied && meta.totalItems > 0;

  return (
    // No scroller in here: the profile page owns the only one (CT-5526), so the
    // pager below is `sticky bottom-0` against THAT, the way `EntityListPage`
    // pins its own. The rows keep the page's 24px gutter, which is also what
    // their hover glow needs — the old inner scroller had to reclaim it with a
    // negative margin because `overflow-y-auto` clipped it.
    <div
      data-testid={`tab-list-${tab.id}`}
      className={['flex min-w-0 flex-1 flex-col gap-4', showPagination ? '' : 'pb-6']
        .filter(Boolean)
        .join(' ')}
    >
      <div className="shrink-0">
        <DataListFilterBar
          filtersState={filtersState}
          filterDefs={filterDefs}
          sortDefs={sortDefs}
          onAllFiltersClick={openAllFilters}
        />
      </div>

      <div className="flex min-w-0 flex-1 flex-col">{results()}</div>

      {showPagination ? (
        /* The breathing room goes on a WRAPPER, not through `Pagination`'s
           `className`: that prop is appended to the bar's own painted box, so a
           `pb-6` there lands inside the pill. */
        <div className="sticky bottom-0 z-10 mt-auto bg-surface-base pb-6">
          <Pagination
            rangeLabel={rangeLabel}
            totalItems={meta.totalItems}
            currentPage={meta.currentPage}
            totalPages={meta.totalPages}
            hasPrevious={meta.hasPrevious}
            hasNext={meta.hasNext}
            perPage={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            onPrevPage={handlePrevPage}
            onNextPage={handleNextPage}
            onPageSizeChange={handlePageSizeChange}
            disabled={loading}
            ariaLabelPerPage={t('list.pagination.rowsPerPage')}
            labels={paginationLabels}
          />
        </div>
      ) : null}

      <DataListFilterSlideout
        isOpen={isAllFiltersOpen}
        onClose={closeAllFilters}
        filtersState={filtersState}
        filterDefs={filterDefs}
        sortDefs={sortDefs}
      />
    </div>
  );
}

export default ProfileDataListTab;
